<template>
  <div id="fileUploadDialog" v-if="visible">
    <div class="dialog-content">
      <div v-if="showIncomingImportPrompt" class="import-confirm-overlay">
        <div class="import-confirm-modal">
          <div class="import-confirm-title">Incoming Bank File Detected</div>
          <div class="import-confirm-message">
            Import <strong>{{ pendingIncomingFileName }}</strong> into
            <strong>{{ incomingAccountLabel }}</strong>?
          </div>
          <div class="import-confirm-actions">
            <button
              class="confirm-btn"
              @click="confirmIncomingImport"
              :disabled="isImportingIncomingFile"
            >
              Yes, import
            </button>
            <button
              class="cancel-btn"
              @click="cancelIncomingImport"
              :disabled="isImportingIncomingFile"
            >
              Not now
            </button>
          </div>
        </div>
      </div>

      <div class="dialog-header">
        <span class="dialog-title">Upload Bank File</span>
      </div>
      <div class="instructions">
        download a single-page HTML file using "printer friendly" view from
        Huntington Bank by right clicking on the printer friendly view and
        selecting "save as". Choose single page MHTML as the type.
      </div>

      <!-- Account selection dropdown -->
      <div class="account-selection">
        <label for="account-select">Select Account:</label>
        <select
          id="account-select"
          class="styled-select"
          v-model="selectedAccount"
        >
          <option
            v-for="account in accountOptions"
            :key="account.accountNumber"
            :value="account.accountNumber"
          >
            {{ account.accountNumber }} - {{ account.accountName }}
          </option>
        </select>
        <button @click="confirmAccount">Select</button>
      </div>

      <!-- Drop zone and browse button -->
      <div v-if="isAccountConfirmed">
        <div id="drop-zone" @dragover.prevent @drop.prevent="handleFileDrop">
          <p>Drop an MHTML file here,</p>
          <p>or</p>
          <p><button @click="triggerFileInput">Browse</button></p>
        </div>
        <input
          type="file"
          ref="fileInput"
          @change="handleFileInput"
          style="display: none"
          accept=".mhtml,.html,.htm"
        />
      </div>

      <div class="instructions">
        As the file is processed, messages will appear here.
      </div>
      <div
        v-if="isProcessingFile || isImportingIncomingFile"
        class="bank-file-spinner"
        role="status"
      >
        <i class="fas fa-spinner fa-spin" aria-hidden="true"></i>
        <span>Processing bank file…</span>
      </div>
      <div class="recent-uploads" v-if="recentUploads.length">
        <div class="recent-uploads-title">Recent bank statement uploads</div>
        <ul>
          <li
            v-for="item in recentUploads.slice(0, 5)"
            :key="`${item.fileName}-${item.uploadedAt}`"
          >
            <span class="recent-file">{{ item.fileName }}</span>
            <span class="recent-date">{{
              formatRecentDate(item.uploadedAt)
            }}</span>
          </li>
        </ul>
      </div>
      <textarea v-model="logMessages" readonly></textarea>
      <div class="form-actions">
        <button @click="runAutomatchOnly">Automatch Only</button>
        <button @click="$emit('close')">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import eventBus from './eventBus.js';
import {
  bankStatementChunkPlan,
  bankStatementNeedsChunks,
} from '@/utils/bankStatementUpload';
import { pickIncomingBankFile } from '@/utils/incomingBankFile';

export default {
  props: {
    visible: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      logMessages: '',
      accountOptions: [],
      selectedAccount: null,
      isAccountConfirmed: false,
      recentUploads: [],
      showIncomingImportPrompt: false,
      pendingIncomingFileName: '',
      isImportingIncomingFile: false,
      isProcessingFile: false,
    };
  },
  computed: {
    incomingAccountNumber() {
      return this.accountNumberFromIncomingFilename(
        this.pendingIncomingFileName,
      );
    },
    incomingAccountLabel() {
      const accountNumber = this.incomingAccountNumber;
      const match = this.accountOptions.find(
        (account) => String(account.accountNumber) === String(accountNumber),
      );
      return match
        ? `${match.accountNumber}-${match.accountName}`
        : accountNumber;
    },
  },
  watch: {
    visible: {
      immediate: true,
      async handler(isVisible) {
        if (!isVisible) return;
        this.logMessages = '';
        if (!this.accountOptions.length) {
          await this.fetchAccounts();
        }
        this.loadRecentUploads();
        await this.inspectIncomingBankStatements();
      },
    },
  },
  methods: {
    async fetchAccounts() {
      try {
        const response = await axios.get('/api/get-bank-accounts', {});
        this.accountOptions = response.data;
        if (this.accountOptions.length > 0) {
          this.selectedAccount = this.accountOptions[0].accountNumber;
        }
      } catch (error) {
        this.logMessages += 'Failed to load accounts.\n';
        console.error('Error fetching accounts:', error);
      }
    },

    confirmAccount() {
      this.isAccountConfirmed = true;
    },

    handleFileInput(event) {
      const file = event.target.files[0];
      this.processFile(file);
    },

    handleFileDrop(event) {
      const file = event.dataTransfer.files[0];
      this.processFile(file);
    },

    async processFile(file) {
      this.logMessages = '';
      if (!file) {
        this.logMessages += 'No file selected.\n';
        return;
      }
      if (this.isProcessingFile || this.isImportingIncomingFile) return;
      this.isProcessingFile = true;
      this.logMessages += 'File loaded successfully.\n';
      try {
        await this.upsertTransactions(file);
        this.logMessages += 'Transactions upserted successfully.\n';
        this.logMessages += 'running Auto-match...\n';
        await this.runAutoMatch();
        this.logMessages += 'Auto-matching completed.\n';
        this.recordRecentUpload(file.name);
        eventBus.emit('bankStatementUpdated');
        this.$emit('success');
      } catch (error) {
        this.logMessages += `Error processing file: ${error.message}\n`;
      } finally {
        this.isProcessingFile = false;
      }
    },

    loadRecentUploads() {
      try {
        const raw = localStorage.getItem('bankStatementRecentUploads');
        const parsed = raw ? JSON.parse(raw) : [];
        this.recentUploads = Array.isArray(parsed) ? parsed.slice(0, 40) : [];
      } catch (error) {
        console.error('Error loading recent uploads from localStorage:', error);
        this.recentUploads = [];
      }
    },

    saveRecentUploads() {
      localStorage.setItem(
        'bankStatementRecentUploads',
        JSON.stringify(this.recentUploads.slice(0, 40)),
      );
    },

    recordRecentUpload(fileName) {
      if (!fileName) return;
      const uploadedAt = new Date().toISOString();
      const withoutDuplicate = this.recentUploads.filter(
        (item) => item.fileName !== fileName,
      );
      this.recentUploads = [
        { fileName, uploadedAt },
        ...withoutDuplicate,
      ].slice(0, 40);
      this.saveRecentUploads();
    },

    formatRecentDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },

    accountNumberFromIncomingFilename(filename) {
      const name = filename || '';
      if (name.includes(' 3723 ')) return '10300';
      if (name.includes(' 3710 ')) return '10000';
      if (name.includes(' 4127 ')) return '10410';
      return '10000';
    },

    async inspectIncomingBankStatements() {
      try {
        const response = await axios.get('/api/bank-statements/incoming-files');
        const files = Array.isArray(response.data?.files)
          ? response.data.files
          : [];
        const targetFile = pickIncomingBankFile(
          files,
          this.recentUploads.map((item) => item.fileName),
        );

        if (!targetFile?.name) return;

        this.pendingIncomingFileName = targetFile.name;
        this.showIncomingImportPrompt = true;
      } catch (error) {
        this.logMessages +=
          'Unable to inspect incoming bank statement directory.\n';
        console.error('Error inspecting incoming bank statements:', error);
      }
    },

    async confirmIncomingImport() {
      const fileName = this.pendingIncomingFileName;
      if (!fileName) {
        this.showIncomingImportPrompt = false;
        return;
      }

      this.showIncomingImportPrompt = false;
      await this.importIncomingFile(fileName);
    },

    cancelIncomingImport() {
      this.showIncomingImportPrompt = false;
      this.pendingIncomingFileName = '';
    },

    async importIncomingFile(fileName) {
      try {
        this.isImportingIncomingFile = true;
        this.logMessages += `Importing ${fileName} from incoming directory...\n`;
        if (/year/i.test(fileName)) {
          this.logMessages +=
            'Large year snapshot — parse + row inserts can take several minutes...\n';
        }
        const accountNumber =
          this.accountNumberFromIncomingFilename(fileName);
        this.selectedAccount = accountNumber;
        this.isAccountConfirmed = true;

        // Year-long MHTMLs need far more than the global 20s axios timeout
        // (server inserts new-format rows one-by-one to preserve order).
        await axios.post(
          '/api/bank-statements/import-incoming',
          {
            fileName,
            accountNumber,
            useTestTable: 'false',
          },
          { timeout: 600000 },
        );
        this.logMessages += `Imported ${fileName} successfully.\n`;
        this.logMessages += 'running Auto-match...\n';
        await this.runAutoMatch();
        this.logMessages += 'Auto-matching completed.\n';
        this.recordRecentUpload(fileName);

        eventBus.emit('bankStatementUpdated');
        this.$emit('success');
      } catch (error) {
        const message = error?.response?.data?.error || error.message;
        const timedOut =
          error?.code === 'ECONNABORTED' ||
          /timeout/i.test(message || '');
        this.logMessages += timedOut
          ? `Error importing ${fileName}: request timed out. Year/large MHTMLs can take several minutes; if this persists check server logs for upsert-transactions progress.\n`
          : `Error importing ${fileName}: ${message}\n`;
        console.error('Error importing incoming file:', error);
      } finally {
        this.isImportingIncomingFile = false;
        this.pendingIncomingFileName = '';
      }
    },

    async upsertTransactions(file) {
      if (bankStatementNeedsChunks(file.size)) {
        let uploadId = '';
        try {
          const plan = bankStatementChunkPlan(file.size);
          this.logMessages += `Uploading ${plan.length} parts...\n`;
          const initResponse = await axios.post(
            '/api/bank-statements/chunked/init',
            {
              fileName: file.name,
              totalChunks: plan.length,
              totalSize: file.size,
            },
            { timeout: 600000 },
          );
          uploadId = initResponse.data && initResponse.data.uploadId;
          if (!uploadId) {
            throw new Error('Chunked upload did not start');
          }
          for (const part of plan) {
            const formData = new FormData();
            formData.append('chunk', file.slice(part.start, part.end), 'chunk');
            formData.append('uploadId', uploadId);
            formData.append('chunkIndex', String(part.index));
            await axios.post('/api/bank-statements/chunked/upload', formData, {
              timeout: 600000,
            });
            this.logMessages += `Uploaded part ${part.index + 1} of ${
              plan.length
            }.\n`;
          }
          const response = await axios.post(
            '/api/upsert-transactions',
            {
              uploadId,
              accountNumber: this.selectedAccount,
              useTestTable: 'false',
            },
            { timeout: 600000 },
          );
          return response.data;
        } catch (error) {
          if (uploadId) {
            try {
              await axios.post(
                '/api/bank-statements/chunked/cancel',
                { uploadId },
                { timeout: 600000 },
              );
            } catch {
              // Cancel is best-effort. A finished upsert already removed the parts.
            }
          }
          console.error('Error inserting transactions:', error);
          const detail = error?.response?.data?.error;
          throw new Error(detail || 'Error inserting transactions');
        }
      }

      try {
        // Read file content explicitly for production compatibility
        const fileContent = await this.readFileContent(file);

        const formData = new FormData();
        // Create a new Blob with the file content and send it as a file
        const blob = new Blob([fileContent], { type: file.type });
        formData.append('file', blob, file.name);
        formData.append('accountNumber', this.selectedAccount);

        const response = await axios.post(
          '/api/upsert-transactions',
          formData,
          { timeout: 600000 },
        );

        return response.data;
      } catch (error) {
        console.error('Error inserting transactions:', error);
        const detail = error?.response?.data?.error;
        throw new Error(detail || 'Error inserting transactions');
      }
    },

    readFileContent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = (error) => reject(error);
        reader.readAsText(file);
      });
    },

    async runAutoMatch() {
      try {
        const selectedAccountOption = this.accountOptions.find(
          (account) => account.accountNumber === this.selectedAccount,
        );

        if (selectedAccountOption && selectedAccountOption.autoMatch === 1) {
          const currentYear = new Date().getFullYear();
          const onOrAfterDate = `${currentYear}-01-01`;

          const response = await axios.get('/api/automatch', {
            params: { onOrAfterDate },
            timeout: 300000,
          });
          return response.data;
        } else {
          this.logMessages +=
            'Auto-match is not enabled for the selected account.\n';
        }
      } catch (error) {
        console.error('Error running auto-match:', error);
        throw new Error('Error running auto-match');
      }
    },

    async runAutomatchOnly() {
      try {
        this.logMessages = 'Starting automatch process...\n';
        await this.runAutoMatch();
        this.logMessages += 'Auto-matching completed successfully.\n';
        eventBus.emit('bankStatementUpdated');
      } catch (error) {
        this.logMessages += `Error running automatch: ${error.message}\n`;
        console.error('Error running automatch only:', error);
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },
  },
  mounted() {
    this.fetchAccounts();
    this.loadRecentUploads();
    this.logMessages = '';
  },
};
</script>

<style scoped>
#fileUploadDialog {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: left;
  align-items: center;
}

.dialog-content {
  background: white;
  padding: 20px;
  border-radius: 5px;
  width: 400px;
  max-height: 80%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-left: 250px;
  box-shadow: 11px 9px 7px 2px rgb(0 0 0 / 32%);
  position: relative;
}

.dialog-header {
  margin: -20px 0px 10px 0px;
  background-color: #907793;
  color: white;
  text-align: center;
  padding: 5px;
  border-top-left-radius: 5px;
  width: 430px;
  border-top-right-radius: 5px;
}

.dialog-title {
  font-size: 1.8em;
}

.account-selection {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

#drop-zone {
  border: 2px dashed #ccc;
  width: 100%;
  height: 100px;
  align-items: center;
  margin-bottom: 10px;
  padding: 10px;
  text-align: center;
}

textarea {
  width: 100%;
  height: 100px;
  resize: none;
}

.form-actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.form-actions button {
  background-color: #907793;
  color: white;
  border: none;
  min-width: 120px;
  padding: 8px 12px;
  border-radius: 3px;
  cursor: pointer;
}

.form-actions button:hover {
  background-color: #7a6684;
}

.dialog-content .instructions {
  font-size: 0.8em;
  font-style: italic;
  margin-bottom: 10px;
}

.bank-file-spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin-bottom: 10px;
  color: #907793;
  font-weight: 600;
}

.bank-file-spinner .fa-spinner {
  font-size: 1.4em;
}

.recent-uploads {
  width: 100%;
  margin-bottom: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 6px 8px;
  background: #fafafa;
}

.recent-uploads-title {
  font-size: 0.8em;
  font-weight: 600;
  margin-bottom: 4px;
}

.recent-uploads ul {
  margin: 0;
  padding-left: 16px;
  font-size: 0.75em;
}

.recent-uploads li {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  margin: 2px 0;
}

.recent-file {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recent-date {
  color: #666;
  white-space: nowrap;
}

.styled-select {
  border-radius: 5px;
  border: 1px solid #c6c6c6;
  padding: 4px;
}

.import-confirm-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.import-confirm-modal {
  width: 85%;
  background: #fff;
  border-radius: 6px;
  padding: 14px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.import-confirm-title {
  font-weight: 700;
  margin-bottom: 8px;
}

.import-confirm-message {
  font-size: 0.9em;
  margin-bottom: 12px;
}

.import-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.import-confirm-actions button {
  border: none;
  border-radius: 4px;
  padding: 6px 10px;
  cursor: pointer;
}

.confirm-btn {
  background: #907793;
  color: #fff;
}

.cancel-btn {
  background: #e7e7e7;
  color: #333;
}
</style>
